import React, { useEffect } from 'react';
import { SignedIn, SignedOut, RedirectToSignIn, SignOutButton, useUser } from '@clerk/clerk-react';
import { Navigate } from 'react-router-dom';
import { setSentryUser } from '@/config/sentry';
import { useServerRole } from '@/domain/auth/hooks/use-server-role';

interface PrivateRouteProps {
  children: React.ReactNode;
  requiredRole?: 'admin' | 'user';
}

const RoleGate: React.FC<{ children: React.ReactNode; requiredRole: 'admin' | 'user' }> = ({ children, requiredRole }) => {
  const { role, isLoading, error, retry } = useServerRole();
  if (isLoading) return <p role="status">Checking access…</p>;
  if (error) return (
    <div role="alert">
      <p>Unable to verify your access.</p>
      <button type="button" onClick={() => retry()}>Try again</button>
    </div>
  );
  if (role !== requiredRole) return <Navigate to="/app" replace />;
  return <>{children}</>;
};

// Signed-in users without a company see why instead of a form that would fail.
// The server makes the decision (auto-joining verified company email domains)
// and enforces it again on every order request.
const CompanyGate: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { role, hasCompany, isLoading, error, retry } = useServerRole();
  if (isLoading) return <p role="status">Checking access…</p>;
  if (error) return (
    <div role="alert">
      <p>Unable to verify your access.</p>
      <button type="button" onClick={() => retry()}>Try again</button>
    </div>
  );
  if (role === 'admin' || hasCompany !== false) return <>{children}</>;
  return (
    <main className="mx-auto max-w-xl px-4 py-16 text-center">
      <h1 className="text-2xl font-semibold">Your company isn't set up yet</h1>
      <p className="mt-3 text-muted-foreground">
        Your account is signed in, but your company hasn't been set up in DocVerifAI yet.
        We've been notified and will get you access shortly.
      </p>
      <div className="mt-6 flex justify-center gap-3">
        <button type="button" className="rounded-md border px-4 py-2" onClick={() => retry()}>Check again</button>
        <SignOutButton><button type="button" className="rounded-md border px-4 py-2">Sign out</button></SignOutButton>
      </div>
    </main>
  );
};

export const PrivateRoute: React.FC<PrivateRouteProps> = ({ children, requiredRole }) => {
  const { user, isLoaded } = useUser();

  // Set Sentry user context when auth resolves
  useEffect(() => {
    if (isLoaded && user) {
      setSentryUser(user.id, user.primaryEmailAddress?.emailAddress);
    }
  }, [isLoaded, user]);

  // Wait for the user to load
  if (!isLoaded) {
    return null;
  }

  return (
    <>
      <SignedIn>
        {requiredRole
          ? <RoleGate requiredRole={requiredRole}>{children}</RoleGate>
          : <CompanyGate>{children}</CompanyGate>}
      </SignedIn>
      <SignedOut>
        <RedirectToSignIn />
      </SignedOut>
    </>
  );
}; 
